import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-link.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/fonts/fonts.js';
import { Price } from '@adornis/payments/db/prices.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { config } from '@ils/config/config.js';
import { getCompanyInfo } from '@ils/config/project-config.js';
import { ExamType } from '@ils/static/enums.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { DateTime } from 'luxon';
import { combineLatest, firstValueFrom, of, timer } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { EventLogSignupPaymentStart, EventLogSignupSelectExamDay } from '../../db/eventlog.js';
import {
  ExamDayReservation,
  subscribeReservationBySessionAndExamDay,
  tryCreateReservation,
} from '../../db/examday-reservation.js';
import { ExamDay } from '../../db/examdays.js';
import type { ILSUser } from '../../db/ils-user.js';
import { getIncrementingOrderNumber, Order } from '../../db/order.js';
import '../components/ils-buy-selected-product.js';
import '../components/ils-loader.js';
import '../components/ils-product-details-card.js';
import '../components/ils-product-flow-header.js';
import '../components/ils-product-list.js';
import '../components/ils-signup-missing.js';
import '../components/ils-signup.js';
import { logout } from '../logout.js';

@customElement('ils-product-page')
export class ILSProductPage extends ChemistryLitElement {
  override styles() {
    return [
      {
        ':host': { display: 'block', height: '100%', width: '100%', paddingTop: '2px' },
        '@keyframes rotateX': {
          from: { transform: 'rotateY(0)' },
          to: { transform: 'rotateY(360deg)' },
        },
      },
    ];
  }

  @property({ type: String, attribute: 'exam-type' }) private readonly examType = new RXController(
    this,
    ExamType.FAMED,
  );

  @state() private readonly examDays = new RXController(
    this,
    this.examType.observable.pipe(
      switchMap(examType =>
        ExamDay.subscribeAllPublishedExamDays(examType)({
          name: 1,
          start: 1,
          end: 1,
          prices: Price.allFields,
          candidateIDs: 1,
          maxCandidates: 1,
          ratingType: 1,
          examType: 1,
          activeReservationsCount: 1,
          location: {
            address: {
              streetAndHousenumber: 1,
              name: 1,
              zipCity: {
                zip: 1,
                city: 1,
              },
              bundesland: 1,
              country: 1,
              additionalInfo: 1,
            },
          },
        }),
      ),
    ),
  );
  @state() private readonly selectedProduct = new RXController<Maybe<ExamDay>>(this, undefined as Maybe<ExamDay>);

  @state() private readonly processingOrder = new RXController(this, false as boolean);

  @state() private readonly user = new RXController(this, currentUser<ILSUser>());

  @state() private readonly reservation = new RXController(
    this,
    combineLatest([this.user.observable, this.selectedProduct.observable]),
    obs =>
      obs.pipe(
        switchMap(([user, examDay]) => {
          if (!user || !examDay) return of(null);
          return subscribeReservationBySessionAndExamDay(examDay._id)(selectionSet(() => ExamDayReservation, 1));
        }),
      ),
  );

  override connectedCallback() {
    super.connectedCallback();

    this.examType.observable.pipe(takeUntil(this.disconnected)).subscribe(examType => {
      DesignSystem.useTheme(examType === ExamType.FSPP ? 'fspp' : 'famed');
    });

    routingState.searchParams
      .get('id')
      .pipe(
        switchMap(payment => (payment ? subscribeByID(Order, payment)({ status: 1 }) : of(null))),
        filter(Boolean),
        filter(payment => payment.status === 'paid' || payment.status === 'canceled' || payment.status === 'refund'),
        take(1),
        takeUntil(this.disconnected),
      )
      .subscribe(payment => {
        if (payment.status === 'paid') {
          this.fireAdwordConversionEvent();
          // todo: use environment here, e.g. isDevelopment
          window.open(getCompanyInfo(this.examType.value).paymentSuccessUrl, '_self');
        } else window.open(getCompanyInfo(this.examType.value).paymentFailureUrl, '_self');
      });

    window.addEventListener('beforeunload', async () => {
      if (!this.reservation.value) return;
      await this.reservation.value.remove();
    });
  }

  /**
   * Helper to send a conversion event to google for Adwords tracking.
   */
  private fireAdwordConversionEvent(orderID: string = '') {
    if (A.isProduction) {
      logger.info('Firing Adwords conversion event..');
      globalThis.gtag('event', 'conversion', {
        send_to: 'AW-716896371/WFPwCKygtq4DEPPw69UC',
        transaction_id: orderID,
      });
    }
  }

  override render() {
    if (this.selectedProduct.value && !this.user.value) return this.signUpPage();
    if (this.selectedProduct.value)
      return html`
        <ils-buy-selected-product
          .examDay=${this.selectedProduct.value}
          ?processingOrder=${this.processingOrder.value}
          @buy=${async () => {
            if (this.user.value && this.selectedProduct.value)
              await this.buyExamDay(this.user.value, this.selectedProduct.value);
          }}
          @logout=${() => logout()}
          @back=${() => this.selectedProduct.next(null)}
        >
        </ils-buy-selected-product>
      `;

    return html`
      <x-flex horizontal space="md">
        <x-flex space="lg" flex>
          ${this._breadcrumb()}
          <ils-product-list
            .examDays=${this.examDays}
            exam-type=${this.examType.value}
            @product-selected=${(c: CustomEvent<{ examDay: ExamDay }>) => this._onProductSelected(c.detail.examDay)}
          >
          </ils-product-list>
        </x-flex>
      </x-flex>
    `;
  }

  protected async _onProductSelected(examDay: ExamDay) {
    const reservation = await tryCreateReservation(examDay._id)(selectionSet(() => ExamDayReservation, 1));
    if (reservation) {
      this.selectedProduct.next(examDay);
      await new EventLogSignupSelectExamDay({ examDayID: examDay._id }).create();
    } else {
      void XSnackbar.show('Prüfungstag bereits ausgebucht.');
    }
  }

  public signUpPage() {
    return html`
      <x-flex flex space="md">
        <ils-product-flow-header
          .examDay=${this.selectedProduct.value}
          @back=${() => this.selectedProduct.next(undefined)}
        >
        </ils-product-flow-header>
        ${this._breadcrumb()}
        <ils-signup
          exam-type=${this.examType.value || ExamType.FAMED}
          @cancel=${() => this.selectedProduct.next(undefined)}
        ></ils-signup>
      </x-flex>
    `;
  }

  private _breadcrumb() {
    if (!this.selectedProduct.value) return nothing;
    return html`
      <ils-product-details-card
        ${acss({
          maxWidth: '300px',
          alignSelf: 'center',
          '@media (max-width: 600px)': {
            width: '100%',
            maxWidth: 'none',
          },
        })}
        .examDay=${this.selectedProduct.value}
      >
      </ils-product-details-card>
    `;
  }

  private async buyExamDay(user: ILSUser, examDay: ExamDay) {
    void new EventLogSignupPaymentStart({ userID: user._id, examDayID: examDay._id }).create();
    this.processingOrder.next(true);

    void XDialog.waitFor(firstValueFrom(this.processingOrder.observable.pipe(filter(x => !x))));

    try {
      console.debug('got here, trying to pay');
      const orderNumber = await getIncrementingOrderNumber(DateTime.now(), examDay.examType ?? ExamType.FAMED);
      if (this.reservation.value) void this.reservation.value.startPaymentProcess();
      await new Order({ orderNumber }).pay({
        scope: examDay.examType ?? ExamType.FAMED,
        paymentMethods: ['paypal', 'klarna', 'card'],
        products: [
          {
            amount: 1,
            product: examDay,
            priceID: examDay.prices[0]!._id /* description: `Rechnungsnummer: ${invoiceNumber}` */,
          },
        ],
        cancelURL: getCompanyInfo(examDay.examType ?? ExamType.FAMED).paymentFailureUrl,
        successURL: getCompanyInfo(examDay.examType ?? ExamType.FAMED).paymentSuccessUrl,
      });
    } catch (error: any) {
      await XDialog.alert(
        `Während wir Ihre Registrierung und Bezahlung verarbeitet haben, ist ein Fehler aufgetreten, bitte senden Sie uns einen Screenshot oder den folgenden Text an ${config.get(
          'TECHNICALSUPPORTMAIL',
        )}: ` +
          (error?.message ?? error?.toString()) +
          '// ' +
          DateTime.now().toFormat('dd.LL.yyyy hh:mm'),
      );
      this.processingOrder.next(false);
      logger.error(error, 'general error while registering or paying');
      void XSnackbar.show('Beim der Registrierung ist etwas schief gelaufen.');
    }

    // Wait for redirect to happen
    void firstValueFrom(timer(5000)).then(() => this.processingOrder.next(false));
  }
}
